import React, { useState, useEffect } from 'react';
import { Euro, Calculator, X } from 'lucide-react';

function CostCalculator({ 
  resultat, 
  dimensionsPlaque,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [prixPlaque, setPrixPlaque] = useState(() => {
    // Charger depuis localStorage si disponible
    const saved = localStorage.getItem('placoplatre_prix_plaque');
    return saved ? parseFloat(saved) : 15; // Prix par défaut : 15€
  });
  const [prixMainOeuvre, setPrixMainOeuvre] = useState(() => {
    const saved = localStorage.getItem('placoplatre_prix_main_oeuvre');
    return saved ? parseFloat(saved) : 35; // Prix au m² par défaut : 35€
  });

  // Sauvegarder les prix dans localStorage
  useEffect(() => {
    localStorage.setItem('placoplatre_prix_plaque', prixPlaque.toString());
  }, [prixPlaque]);

  useEffect(() => {
    localStorage.setItem('placoplatre_prix_main_oeuvre', prixMainOeuvre.toString());
  }, [prixMainOeuvre]);

  if (!resultat) return null;

  // Calculs
  const surfacePlaqueCm2 = dimensionsPlaque.largeur * dimensionsPlaque.hauteur; // cm²
  const surfacePlaque = surfacePlaqueCm2 / 10000; // en m²
  const prixAuM2 = prixPlaque / surfacePlaque;

  // Seules les plaques neuves sont achetées. Les chutes réemployées sont des
  // morceaux issus de ces plaques : elles ne s'ajoutent pas au coût.
  const nbPlaques = resultat.nbPlaques;
  const economieChutes = resultat.economieChutes || { nbChutesUtilisees: 0, surfaceChutesUtilisees: 0 };
  const nbPlaquesAchetees = nbPlaques;

  const coutPlaques = nbPlaquesAchetees * prixPlaque;

  // Économie estimée : surface couverte par des chutes, convertie en équivalent
  // plaques neuves qu'on aurait dû acheter sans réutilisation.
  const plaquesEconomisees = economieChutes.surfaceChutesUtilisees / surfacePlaqueCm2;
  const economieChutesEuros = plaquesEconomisees * prixPlaque;

  const surfaceUtileM2 = resultat.surfaceUtile / 10000;
  const coutMainOeuvre = surfaceUtileM2 * prixMainOeuvre;

  const coutTotal = coutPlaques + coutMainOeuvre;

  return (
    <div className={className}>
      <button 
        className="btn btn-secondary"
        onClick={() => setIsOpen(true)}
        title="Calculer le coût"
      >
        <Euro size={18} style={{ marginRight: '8px' }} />
        Coût
      </button>

      {isOpen && (
        <div className="modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Calcul du coût</h3>
              <button className="btn-icon" onClick={() => setIsOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Configuration des prix */}
              <div className="cost-config" style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Configuration des prix</h4>
                
                <div className="form-row">
                  <div className="form-group half">
                    <label>Prix d'une plaque</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={prixPlaque}
                        min="0"
                        step="0.5"
                        onChange={(e) => setPrixPlaque(parseFloat(e.target.value) || 0)}
                      />
                      <div className="input-group-append">€</div>
                    </div>
                    <small style={{ color: '#666' }}>
                      {surfacePlaque.toFixed(2)} m² = {prixAuM2.toFixed(2)}€/m²
                    </small>
                  </div>
                  
                  <div className="form-group half">
                    <label>Main d'œuvre</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={prixMainOeuvre}
                        min="0"
                        step="1"
                        onChange={(e) => setPrixMainOeuvre(parseFloat(e.target.value) || 0)}
                      />
                      <div className="input-group-append">€/m²</div>
                    </div>
                    <small style={{ color: '#666' }}>
                      Pose et finition
                    </small>
                  </div>
                </div>
              </div>

              {/* Résumé des coûts */}
              <div className="cost-summary">
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Résumé</h4>
                
                <div className="cost-item" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Plaques neuves à acheter:</span>
                  <span><strong>{nbPlaquesAchetees}</strong>{economieChutes.nbChutesUtilisees > 0 && <> (+ {economieChutes.nbChutesUtilisees} chutes réemployées)</>}</span>
                </div>
                
                <div className="cost-item" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Coût des plaques:</span>
                  <span><strong>{coutPlaques.toFixed(2)} €</strong></span>
                </div>
                
                {economieChutes.nbChutesUtilisees > 0 && (
                  <div className="cost-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: '1px solid #eee',
                    color: '#28a745'
                  }}>
                    <span>Économie (chutes):</span>
                    <span><strong>- {economieChutesEuros.toFixed(2)} €</strong></span>
                  </div>
                )}
                
                <div className="cost-item" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Surface utile:</span>
                  <span><strong>{surfaceUtileM2.toFixed(2)} m²</strong></span>
                </div>
                
                <div className="cost-item" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Main d'œuvre:</span>
                  <span><strong>{coutMainOeuvre.toFixed(2)} €</strong></span>
                </div>
                
                <div className="cost-total" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '15px 0',
                  marginTop: '10px',
                  borderTop: '2px solid #333',
                  fontSize: '1.2em',
                  fontWeight: 'bold'
                }}>
                  <span>Coût total:</span>
                  <span>
                    {coutTotal.toFixed(2)} €
                  </span>
                </div>
                
                {economieChutes.nbChutesUtilisees > 0 && (
                  <div style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '10px',
                    borderRadius: '4px',
                    marginTop: '10px',
                    fontSize: '0.9em'
                  }}>
                    💰 Vous économisez <strong>{economieChutesEuros.toFixed(2)} €</strong> en réutilisant des chutes !
                  </div>
                )}
              </div>

              {/* Détails par mur */}
              <div className="cost-details" style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '15px' }}>Détail par mur</h4>
                
                {resultat.murDetails?.map((detail) => {
                  const nbPlaquesMur = detail.nbPlaquesNeuves || 0;
                  const nbChutesMur = detail.nbChutesUtilisees || 0;
                  const surfaceMur = detail.surfaceUtile / 10000;
                  const coutMur = nbPlaquesMur * prixPlaque + surfaceMur * prixMainOeuvre;

                  return (
                    <div key={detail.murId} className="mur-cost-item" style={{
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {detail.murNom || `Mur ${detail.murId}`}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {nbPlaquesMur} plaques neuves{nbChutesMur > 0 ? ` + ${nbChutesMur} chutes` : ''} | {surfaceMur.toFixed(2)} m²
                      </div>
                      <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {coutMur.toFixed(2)} €
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CostCalculator;
